import React, { useEffect, useState } from 'react'
import {
  Box,
  CardMedia,
  Dialog,
  IconButton,
  Skeleton,
  Tab,
  Tooltip,
  useMediaQuery,
  Grid,
} from '@mui/material'
import DefaultEventImage from '../../../../../assets/images/DefaultEventImage.png'
import RemoveRedEyeRoundedIcon from '@mui/icons-material/RemoveRedEyeRounded'
import CloseIcon from '@mui/icons-material/Close'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { useTheme } from '@mui/system'
import { useFetchImageUrl } from '../../../../../hooks/componentHooks/useFetchImageUrl'
import { textColors } from '../../../../Common/Color/Color'
import { fontSizes, fontWeights } from '../../../../Common/Typography/TypographyStyles'
import EventDetailsTab from './EventViewTabPanel/EventDetailsTab'
import EventShareTab from './EventViewTabPanel/EventShareTab'
import { fetchEventDetails } from '../../../../../redux/event/manageEvents/actions'
import { useAppDispatch, useAppSelector } from '../../../../../redux/store'
import {
  selectSelectedEvent,
  selectSelectedEventLoading,
} from '../../../../../redux/event/manageEvents/selector'
import { clearSelectedEvent } from '../../../../../redux/event/manageEvents/slice'
import { fetchTeamMemberInfo } from '../../../../../redux/teamMember/actions'
import { clearTeamMemberInfo } from '../../../../../redux/teamMember/slice'
import { selectLoadingTeamMemberInfo } from '../../../../../redux/teamMember/selector'

interface CustomizeTabPanelProps {
  value: string
  children: React.ReactNode
  fullScreen: boolean
}

const CustomizeTabPanel: React.FC<CustomizeTabPanelProps> = ({ value, children, fullScreen }) => {
  return (
    <TabPanel value={value}>
      <Box
        sx={{
          ...(!fullScreen && {
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#5f2b7f',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#555',
            },
            scrollbarWidth: 'auto',
            msOverflowStyle: 'auto',
          }),
        }}
      >
        {children}
      </Box>
    </TabPanel>
  )
}

const DialogSkeleton = () => {
  return (
    <Grid container size={12}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Skeleton variant='rectangular' height='500px' />
      </Grid>
      <Grid></Grid>
    </Grid>
  )
}

interface EventViewModalProps {
  id: string
}

const EventViewModal: React.FC<EventViewModalProps> = ({ id }) => {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const dispatch = useAppDispatch()

  const [open, setOpen] = useState(false)
  const [value, setValue] = React.useState('1')

  const selectedEvent = useAppSelector(selectSelectedEvent)
  const loading = useAppSelector(selectSelectedEventLoading)
  const fetchTeamMemberInfoLoading = useAppSelector(selectLoadingTeamMemberInfo)

  useEffect(() => {
    const fetchEventDetail = async () => {
      if (open) {
        await dispatch(fetchEventDetails(id))
      }
    }
    fetchEventDetail()
  }, [open])

  useEffect(() => {
    const fetchTeamMember = async () => {
      if (open && selectedEvent?.createdBy?.workdayId && selectedEvent?.createdBy?.email) {
        try {
          await dispatch(
            fetchTeamMemberInfo({
              workdayId: selectedEvent.createdBy.workdayId,
              email: selectedEvent.createdBy.email,
            }),
          )
        } catch (error) {
          console.error('Failed to fetch team member info:', error)
        }
      }
    }

    fetchTeamMember()
  }, [open, selectedEvent?.createdBy?.workdayId && selectedEvent?.createdBy?.email])

  const { imageUrl, loading: fetchImageUrlLoading } = useFetchImageUrl(
    'images',
    selectedEvent?.imageFilename ?? '',
    'event',
    open,
  )

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue)
  }

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    dispatch(clearSelectedEvent())
    dispatch(clearTeamMemberInfo())
  }

  const isLoading = loading || fetchImageUrlLoading || fetchTeamMemberInfoLoading
  return (
    <React.Fragment>
      <Tooltip title='View' arrow>
        <IconButton id='preview-event' color='success' size='small' onClick={handleOpen}>
          <RemoveRedEyeRoundedIcon />
        </IconButton>
      </Tooltip>

      <Dialog
        open={open}
        aria-labelledby='event-dialog-title'
        fullWidth={!fullScreen}
        maxWidth='lg'
        fullScreen={fullScreen}
      >
        {isLoading ? (
          <DialogSkeleton />
        ) : open && selectedEvent ? (
          <Grid container>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box position='relative' sx={{ height: '500px', overflow: 'hidden' }}>
                <CardMedia
                  component='img'
                  image={imageUrl ? imageUrl : DefaultEventImage}
                  height='100%'
                  alt='event-card-preview'
                  sx={{ objectFit: 'cover', zIndex: 0 }}
                />

                <Box
                  position='absolute'
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  p={2}
                  sx={{
                    display: 'flex',
                    justifyContent: 'start',
                    alignItems: 'end',
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    color: textColors.white,
                    fontSize: fontSizes.size16,
                    fontWeight: fontWeights[700],
                  }}
                >
                  {selectedEvent.title}
                </Box>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <IconButton
                onClick={handleClose}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 1,
                }}
                size='small'
              >
                <CloseIcon />
              </IconButton>
              <Box p={2}></Box>
              <Box sx={{ width: '100%' }}>
                <TabContext value={value}>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <TabList onChange={handleChange} aria-label='Event tabs' centered>
                      <Tab label='Event Details' value='1' />
                      <Tab label='Event Share: Link & QR' value='2' />
                    </TabList>
                  </Box>
                  <CustomizeTabPanel value='1' fullScreen={fullScreen}>
                    <EventDetailsTab eventDetail={selectedEvent} />
                  </CustomizeTabPanel>
                  <CustomizeTabPanel value='2' fullScreen={fullScreen}>
                    <EventShareTab eventDetail={selectedEvent} />
                  </CustomizeTabPanel>
                </TabContext>
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Dialog
            open={open}
            aria-labelledby='event-dialog-title'
            fullWidth={!fullScreen}
            maxWidth='lg'
            fullScreen={fullScreen}
          >
            No Data
          </Dialog>
        )}
      </Dialog>
    </React.Fragment>
  )
}

export default EventViewModal
